<template>
  <div
    class="c-book__wrapper"
    v-shortkey="{
      left: ['arrowleft'],
      right: ['arrowright'],
      up: ['arrowup'],
      down: ['arrowdown'],
    }"
    @shortkey="keyAction"
  >
    <div class="c-book__bg"></div>
    <div class="c-book__assets"></div>
    <book-menu
      v-if="isDesktop"
      :showBars="showBars"
      :modalIsOpen="modalIsOpen"
    />
    <book-topbar
      v-if="!isDesktop"
      :connection="connection"
      :showBars="showBars"
      @toggle-bars="toggleBars"
    />
    <book-menu-content-box v-if="modalIsOpen"></book-menu-content-box>
    <div
      class="c-book__book-container"
      ref="bookContainer"
      :class="[
        {
          'bottom-bar': showBars,
          thumbnails: showThumbnails,
          'is-html': isHTML,
        },
        responsive,
      ]"
    >
      <div
        v-if="book"
        class="c-book__page page-wrap"
        :style="`width: ${wrapperWidth};`"
        :class="[{ rtl: bookDirection }]"
      >
        <book-title
          v-if="!connection"
          :showBars="showBars"
          :connection="connection"
          :responsive="responsive"
        />
        <book-page
          v-for="(item, index) in content"
          :key="item + index"
          :pageIndex="index"
          :showBars="showBars"
          :htmlPage="item"
          :zoom-level="zoomLevel"
          :defaultBookWidth="defaultBookWidth"
          :bookRender="bookRender"
          :bookmarks="bookmarks"
          :fetchBookmarks="fetchBookmarks"
          :fitToWindow="fitToWindow"
          :isHTML="isHTML"
          :accessToken="accessToken"
          :assetsLoaded="assetsLoaded"
          :connection="connection"
          :bookContainerHeight="bookContainerHeight"
          @assets-loaded="assetsLoaded = true"
          @term-search="updateTerm"
          @lookup-term="showLookup"
          @page-container-width="pageContainerWidth = $event"
          @fit-zoom-level="fitZoom($event, index)"
        />
        <div class="c-book__page-arrows">
          <book-page-arrow
            class="prev"
            @click.native="turnPage('previous')"
            :aria-label="`${$t('GO_TO')} ${$t('PREVIOUS_PAGE')}`"
          ></book-page-arrow>
          <book-page-arrow
            class="next"
            @click.native="turnPage('next')"
            :aria-label="`${$t('GO_TO')} ${$t('NEXT_PAGE')}`"
          ></book-page-arrow>
        </div>
        <book-zoom-bar
          v-if="isZoomable"
          :fitToWindow="fitToWindow"
          @fit-window="fitToWindow = true"
          @update-zoom-level="explicitZoom($event)"
        />
        <book-bottombar
          ref="bookBottombar"
          :isOpen="showThumbnails"
          :showBars="showBars"
          :pagesPerView="pagesPerView"
          :bookRender="bookRender"
          :bookDirection="bookDirection"
          :showThumbnails="showThumbnails"
          :responsive="responsive"
          :access-token="accessToken"
          :book="book"
          :is-desktop="isDesktop"
          @toggle-bars="toggleBars"
          @toggle-thumbnails="toggleThumbnails()"
          @change-page="turnPage($event)"
          @change-pages-limit="updateSlider"
          @close-thumbnails="closeThumbnails"
        />
        <div
          class="c-book-menu__overlay"
          v-if="isDesktop && modalIsOpen"
          @click="closeModal()"
        ></div>
        <lookup
          ref="lookup"
          v-if="term !== null"
          :term="term"
          :book-source="bookSource"
          :book-lang="bookLang"
          v-click-outside="{
            exclude: ['lookup'],
            handler: 'closeLookup',
          }"
          @close-lookup="term = null"
        >
        </lookup>
      </div>
      <div v-else>
        Book loading...
      </div>
    </div>
    <div
      v-if="!isDesktop"
      ref="pagesLimitWrapper"
      class="c-book__pages-limit-wrapper"
    >
      <button class="c-book__pages-limit-btn" @click="turnPage('previous')">
        <back-arrow class="c-book__pages-limit-arrow" fill="#363a5e" />
      </button>
      <book-pages-limit
        :is-two-page-layout="isTwoPageLayout"
        :book-render="bookRender"
        :book-direction="bookDirection"
        @change-pages-limit="updateSlider"
        @select-pages-limit="selectText"
      ></book-pages-limit>
      <button class="c-book__pages-limit-btn" @click="turnPage('next')">
        <back-arrow
          class="c-book__pages-limit-arrow c-book__pages-limit-arrow--right"
          fill="#363a5e"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import matchAll from 'string.prototype.matchall'

import { UserDB } from '@/services/db'
import store from '@/store'
import HpubRenderer from '@/renderer/hpub'
import { isEdgeAgent, isSafariAgent, setDocumentTitle } from '@/utils/utils'
import i18n from '@/i18n/index'

import BackArrow from '@/assets/svg/BackArrow'
import BookPageArrow from '@/components/BookPageArrow.vue'

import BookMenu from '@/components/BookMenu'
import BookTopBar from '@/components/BookTopBar'
import BookBottomBar from '@/components/BookBottomBar'
import BookPage from '@/components/BookPage'
import BookTitle from '@/components/BookTitle'
import BookMenuContentBox from '@/components/BookMenuContentBox'
import BookZoomBar from '@/components/BookZoomBar'
import Lookup from '@/components/Lookup'
import BookPagesLimit from '@/components/BookPagesLimit'
import { getBookMetadata } from '@/utils/utils'

let db = new UserDB()
// Force loading book page overlay to hide after 5s, even if some files on server are corrupted.
const TIME_TO_FORCE_ASSETS_LOAD = 5000
const DEFAULT_BOOK_WIDTH = 1325
const DEFAULT_BOOK_HEIGHT = 968

export default {
  name: 'Book',

  beforeDestroy() {
    this.book.title = ''
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  },

  destroyed() {
    clearTimeout(this.forceAssetsLoadTimeout)
  },

  data() {
    return {
      showBars: true,
      bookRender: null,
      content: [],
      wrapperWidth: '100%',
      defaultBookWidth: DEFAULT_BOOK_HEIGHT,
      pageContainerWidth: DEFAULT_BOOK_WIDTH,
      fitToWindow: true,
      showThumbnails: false,
      assetsLoaded: false,
      sidebarToggled: true,
      forceAssetsLoadTimeout: null,
      term: null,
      bookSource: null,
      bookLang: {},
      bookContainerHeight: null,
      resizeObserver: null,
      pagesZooms: [],
    }
  },

  components: {
    'book-menu': BookMenu,
    'book-bottombar': BookBottomBar,
    'book-page': BookPage,
    'book-zoom-bar': BookZoomBar,
    'book-title': BookTitle,
    'book-menu-content-box': BookMenuContentBox,
    'book-topbar': BookTopBar,
    lookup: Lookup,
    'book-pages-limit': BookPagesLimit,
    'back-arrow': BackArrow,
    'book-page-arrow': BookPageArrow,
  },

  computed: {
    isDesktop() {
      return this.responsive === 'desktop'
    },
    bookDirection() {
      return getBookMetadata(this.book, '-dbook-right-to-left', false)
    },

    isHTML() {
      return getBookMetadata(this.book, '-dbook-html-content', false) == 'true'
        ? true
        : false
    },

    isZoomable() {
      return getBookMetadata(this.book, 'zoomable', true)
    },

    isTwoPageLayout() {
      const currentPage = this.bookRender
        ? this.bookRender.currentPageNumber
        : 1
      return (
        this.pagesPerView === 2 &&
        currentPage !== 1 &&
        currentPage !== this.bookRender.getTotalPages()
      )
    },

    isDownloaded() {
      if (this.savedBooks.indexOf(this.$route.params.id) > -1) {
        return true
      }
      return false
    },

    ...mapState('book', {
      book: state => state.book,
      savedBooks: state => state.savedBooks,
      books: state => state.books,
    }),

    ...mapState('bookmarks', {
      bookmarks: state => state.bookmarks,
    }),

    ...mapState('bookMenu', {
      modalIsOpen: state => state.isOpen,
    }),

    ...mapState('userSettings', {
      zoomLevel: state => state.zoomLevel,
      pagesPerView: state => state.pagesPerView,
      lastReadPage: state => state.lastReadPage,
      connection: state => state.connection,
      responsive: state => state.responsive,
    }),

    ...mapState('user', {
      accessToken: state => state.accessToken,
    }),
  },

  async created() {
    await this.getSavedBooks()
    if (!this.books.length) await this.fetchBooks()
    await this.fetchBook(this.$route.params.id)
    // we need to first fetch book in order to check access
    this.checkBookAccess(this.book)
    await this.fetchTOC(this.$route.params.id)
    this.bookRender = new HpubRenderer(this.book, undefined, {
      pagesPerView: this.pagesPerView,
      page: await this.checkPage(),
    })
    this.pageChangeHandler()

    this.clearSettings()

    await this.loadSettings()
    await this.fetchAndSaveBookmarks()
    await this.fetchAllAnnotations()
    await this.getAllAnnotationsFromDB()

    setDocumentTitle(this.book.title)
  },

  async mounted() {
    const locale =
      (await db.getLocale('locale')) || store.state.userSettings.locale || i18n.locale
    this.setLocale(locale)

    this.forceAssetsLoadTimeout = setTimeout(() => {
      this.assetsLoaded = true
    }, TIME_TO_FORCE_ASSETS_LOAD)

    const dictLang = getBookMetadata(
      this.book,
      '-dbook-dictionary-language',
      'nb_NO',
    )

    this.bookSource = getBookMetadata(this.book, '-dbook-resource-domain', null)

    this.bookLang = {
      name: i18n.messages[dictLang.slice(0, 2)].DICT_LANG,
      code: dictLang,
    }

    this.resizeObserver = new ResizeObserver(this.onResize)
    this.resizeObserver.observe(this.$refs.bookContainer)
  },

  methods: {
    onResize() {
      this.fitBooks()
    },
    checkBookAccess(book) {
      // TODO: Access check should only happens at one place.
      // Eportal holds access and it should only check access.
      // However, e-portal doesn't grant access to In Review books.
      if (
        book &&
        book.status === 'REVIEW' &&
        (process.env.VUE_APP_DISABLE_ACCESS_CHECK || false) === 'true'
      ) {
        return
      }

      // This checks if user has access to book in e-portal or not
      if (!this.books.some(book => book.id === this.$route.params.id)) {
        this.$router.push({
          name: 'AccessDenied',
          query: { bookId: this.$route.params.id },
        })
      }
    },

    updateTerm(term) {
      this.term = term
    },

    updateSlider(e) {
      let pattern = /^((\d+)|(\d+)(\/?)(\d+))$/

      if (pattern.test(e.target.value)) {
        if (this.pagesPerView === 2 && parseInt(e.target.value) % 2 === 1) {
          this.bookRender.gotoPage(parseInt(e.target.value) - 1)
        } else {
          this.bookRender.gotoPage(parseInt(e.target.value))
        }
        setTimeout(() => {
          this.$refs.bookContainer.scrollTo(0, 0)
        }, 500)
      }
    },

    selectText(e) {
      e.target.select(0, e.target.value.length)
    },

    keyAction(event) {
      switch (event.srcKey) {
        case 'left':
          //check if scroll is at most left
          if (this.$refs.bookContainer.scrollLeft === 0) {
            this.turnPage('previous')
          } else {
            this.$refs.bookContainer.scrollLeft -= 100
          }
          break
        case 'right':
          //check if scroll is at most right
          if (
            this.$refs.bookContainer.scrollWidth -
              this.$refs.bookContainer.scrollLeft <=
            this.$refs.bookContainer.clientWidth
          ) {
            this.turnPage('next')
          } else {
            this.$refs.bookContainer.scrollLeft += 80
          }
          break
        case 'down':
          this.$refs.bookContainer.scrollTop += 80
          break
        case 'up':
          this.$refs.bookContainer.scrollTop -= 80
          break
        default:
      }
    },

    turnPage(direction) {
      direction === 'next'
        ? this.bookRender.getNextPage()
        : this.bookRender.getPreviousPage()
      setTimeout(() => {
        this.$refs.bookContainer.scrollTo(0, 0)
      }, 500)
    },

    async checkPage() {
      const isInt = new RegExp('^\\d+$')

      if (
        this.$route.params.page === undefined ||
        !isInt.test(this.$route.params.page)
      ) {
        let getLastReadPage
        try {
          getLastReadPage = await db.getSettings(
            this.$route.params.id,
            'lastReadPage',
          )
        } catch (error) {
          // not found in db
          getLastReadPage = this.lastReadPage || 1 // state can be empty
        }

        if (getLastReadPage && getLastReadPage.page) {
          return parseInt(getLastReadPage.page)
        }

        return 1
      }

      return parseInt(this.$route.params.page)
    },

    explicitZoom(value) {
      this.fitToWindow = false
      this.setZoomLevel(value)
    },
    fitZoom(value, index) {
      this.pagesZooms[index] = value
      const minZoom = Math.min(...this.pagesZooms)
      this.setZoomLevel(minZoom ? minZoom : 100)
    },

    fitBooks() {
      const bookLeftRightMargin = 5
      //bookmark is scaling with zoom
      const bookmarkWidth = 60 * this.zoomLevel * 0.01
      //width of single page with bookmark and 5px
      let bookWidth =
        this.pageContainerWidth + bookmarkWidth + bookLeftRightMargin

      if (
        this.bookRender &&
        this.pagesPerView === 2 &&
        this.bookRender.currentPageNumber !== 1 &&
        this.bookRender.currentPageNumber !== this.bookRender.getTotalPages()
      ) {
        bookWidth = bookWidth * 2
      }

      if (!this.isHTML && this.$refs.bookContainer.clientWidth < bookWidth) {
        this.wrapperWidth = bookWidth.toString() + 'px'
      } else {
        this.wrapperWidth = '100%'
      }

      this.bookContainerHeight = this.$refs.bookContainer
        ? this.$refs.bookContainer.clientHeight
        : 0
    },

    toggleThumbnails() {
      this.showThumbnails = !this.showThumbnails
    },

    toggleBars() {
      this.showBars = !this.showBars
      this.showThumbnails = false
    },

    hideBars() {
      this.showBars = false
    },

    async loadSettings() {
      let getPagesPerView = await db.getSettings(
        this.$route.params.id,
        'pagesPerView',
      )
      if (getPagesPerView === undefined) {
        getPagesPerView = this.pagesPerView
      }

      this.setPagesPerView(getPagesPerView)
    },

    pageChangeHandler() {
      this.bookRender.pageChangeHandler().then(content => {
        if (isEdgeAgent() || isSafariAgent()) {
          content[0] = this.replaceImageSrc(content[0])
        }

        this.content = content
      })
    },

    /* Fix for Edge: rendering background image with improper base url */
    replaceImageSrc(content) {
      // matchAll is not supported in IE Edge but polyfills should be added by core-js
      const value = matchAll(
        content,
        /<img\b(?=\s)(?=(?:[^>=]|='[^']*'|="[^"]*"|=[^'"][^\s>]*)*?\ssrc=['"]([^"]*)['"]?)(?:[^>=]|='[^']*'|="[^"]*"|=[^'"\s]*)*"\s?\/?>/gm,
      )
      for (const m of value) {
        content = content.replace(
          m[1],
          `${process.env.VUE_APP_DPM_URL}/streams/${this.$route.params.id}/file/${m[1]}`,
        )
      }

      return content
    },

    showLookup(term) {
      this.term = term
    },

    closeLookup() {
      if (document.querySelector('.c-lookup-box, .c-lookup__empty')) {
        this.term = null
      }
    },

    closeThumbnails() {
      this.showThumbnails = false
      this.$refs.bookBottombar.focusThumbnailToggle()
    },

    ...mapActions({
      fetchBooks: 'book/fetchBooks',
      fetchBook: 'book/fetchBook',
      fetchTOC: 'book/fetchTOC',
      closeModal: 'bookMenu/closeModal',
      setZoomLevel: 'userSettings/setZoomLevel',
      setPagesPerView: 'userSettings/setPagesPerView',
      setLastReadPage: 'userSettings/setLastReadPage',
      clearSettings: 'userSettings/clearSettings',
      fetchBookmarks: 'bookmarks/fetchBookmarks',
      fetchAndSaveBookmarks: 'bookmarks/fetchAndSaveBookmarks',
      fetchAllAnnotations: 'annotations/fetchAllAnnotations',
      getAllAnnotationsFromDB: 'annotations/getAllAnnotationsFromDB',
      setLocale: 'userSettings/setLocale',
      getSavedBooks: 'book/getSavedBooks',
    }),
  },

  watch: {
    '$route.params.page'(page) {
      this.bookRender.getPage(page)
    },

    'bookRender.currentPageNumber'(val) {
      this.$router
        .replace({ name: 'Book', params: { page: val } })
        .catch(err => {
          if (err && err.name !== 'NavigationDuplicated') {
            throw err
          }
        }) // Handles NavigationDuplicated

      this.setLastReadPage({
        page: val,
        id: this.$route.params.id,
        created: Date.now(),
      })

      this.pageChangeHandler()
    },

    'lastReadPage.page'(newVal) {
      if (newVal > 1) {
        this.bookRender.getPage(newVal)
      }
    },

    async pagesPerView(newVal) {
      if (newVal === 2 && !this.isHTML) {
        this.bookRender.twoPages = true
        this.pageChangeHandler()
      } else {
        this.bookRender.twoPages = false
        this.pageChangeHandler()
      }
    },
    pageContainerWidth() {
      this.fitBooks()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_breakpoints.scss';
@import '@/assets/scss/_mixin.scss';
@import '@/assets/scss/_vars.scss';

.c-book__wrapper {
  position: relative;
  overflow: hidden;
  &:focus {
    outline: none;
  }
}
.c-book__book-container {
  position: fixed;
  overflow: auto;
  left: $book-menu-width;
  right: 0;
  top: 0;
  bottom: 0;
  scroll-behavior: smooth;
  &.bottom-bar {
    bottom: $book-bottom-bar-height;
    &.is-html {
      bottom: $book-bottom-bar-height - 10px;
    }
  }
  &.thumbnails {
    bottom: $book-bottom-bar-height + $book-thumbnails-height;
  }
  &.tablet,
  &.mobile {
    left: 0;
    top: $book-top-bar-height;
  }
}

.c-book__bg {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to bottom, $grey 0%, $gradient-blue 100%)
    $gradient-blue repeat-x; /* W3C, IE10+, FF16+, Chrome26+, Opera12+, Safari7+ */
}

.c-book__page {
  position: relative;
  text-align: center;
  margin: 0;
  padding: 5px 0;
  overflow: hidden;
  height: auto;
}

.c-book-menu__overlay {
  position: absolute;
  bottom: 0;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
}

.c-book__pages-limit-wrapper {
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  padding-top: 1rem;
}

.c-book__pages-limit-btn {
  @include btnReset();
  margin-right: 0.75rem;
  cursor: pointer;

  @include a11yOutline();
}

.c-book__page-arrows {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 90%;
  pointer-events: none;
  z-index: 10;

  @media screen and (min-width: 1366px) {
    left: calc(50% + 110px);
    transform: translate(calc(-50% - 55px), -50%);
  }
}

.c-book__pages-limit-arrow {
  &--right {
    transform: rotate(180deg);
  }
}

.rtl {
  direction: rtl;
}
</style>
